import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AgentPromptsState,
  AgentPrompt,
  AgentPromptsListData,
} from '../../types/agentPrompts.types';

const initialState: AgentPromptsState = {
  prompts: [],
  selectedPrompt: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    pageSize: 10,
    totalPages: 0,
    totalRecords: 0,
    hasMorePages: false,
  },
  search: '',
};

const agentPromptsSlice = createSlice({
  name: 'agentPrompts',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setPrompts: (state, action: PayloadAction<AgentPromptsListData>) => {
      state.prompts = action.payload.prompts;
      state.pagination = {
        ...state.pagination,
        totalPages: action.payload.totalPages,
        totalRecords: action.payload.totalRecords,
        hasMorePages: action.payload.hasMorePages,
      };
      state.loading = false;
      state.error = null;
    },
    setSelectedPrompt: (state, action: PayloadAction<AgentPrompt | null>) => {
      state.selectedPrompt = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
    },
    setSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
      state.pagination.page = 1;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedPrompt: (state) => {
      state.selectedPrompt = null;
    },
    addPrompt: (state, action: PayloadAction<AgentPrompt>) => {
      state.prompts.unshift(action.payload);
      state.pagination.totalRecords += 1;
    },
    updatePrompt: (state, action: PayloadAction<AgentPrompt>) => {
      const index = state.prompts.findIndex((p) => p.id === action.payload.id);
      if (index !== -1) {
        state.prompts[index] = action.payload;
      }
      if (state.selectedPrompt?.id === action.payload.id) {
        state.selectedPrompt = action.payload;
      }
    },
    deletePrompt: (state, action: PayloadAction<string>) => {
      state.prompts = state.prompts.filter((p) => p.id !== action.payload);
      state.pagination.totalRecords -= 1;
      if (state.selectedPrompt?.id === action.payload) {
        state.selectedPrompt = null;
      }
    },
    resetAgentPrompts: () => initialState,
  },
});

export const {
  setLoading,
  setError,
  setPrompts,
  setSelectedPrompt,
  setPage,
  setPageSize,
  setSearch,
  clearError,
  clearSelectedPrompt,
  addPrompt,
  updatePrompt,
  deletePrompt,
  resetAgentPrompts,
} = agentPromptsSlice.actions;

export default agentPromptsSlice.reducer;
